import { Badge } from "primereact/badge";
import { Card } from "primereact/card";
import { IContractHistory } from "store/queries/contractFeeSchedules";

import {
  diffNamedList,
  diffRateCalculations,
  formatHistoryValue,
  HISTORY_FIELD_LABELS,
  HISTORY_LIST_FIELDS,
  HISTORY_TYPE_LABELS,
  HISTORY_TYPE_SEVERITY,
} from "../helpers";

interface IContractHistoryProps {
  histories?: IContractHistory[];
}

/** Activity log for a contract: created/updated events, by who, and what changed. */
export default function ContractHistory({ histories }: IContractHistoryProps) {
  // Oldest first.
  const sortedHistories = [...(histories ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return (
    <Card title="Contract History" className="mb-4">
      {sortedHistories?.length > 0 ? (
        <div className="flex flex-column gap-3">
          {sortedHistories.map((entry, index) => (
            <div
              key={entry.id}
              className={
                index < sortedHistories.length - 1 ? "pb-3 border-bottom" : ""
              }
            >
              <div className="flex align-items-center gap-2 mb-1 flex-wrap">
                <Badge
                  value={HISTORY_TYPE_LABELS[entry.type] ?? entry.type}
                  severity={HISTORY_TYPE_SEVERITY[entry.type] ?? "info"}
                />
                <span className="text-900 font-medium">
                  {entry.user?.name || entry.user?.email || "System"}
                </span>
                <span className="text-600 text-sm">
                  {new Date(entry.createdAt).toLocaleString()}
                </span>
              </div>
              {entry.description && (
                <div className="text-700 text-sm mb-1">{entry.description}</div>
              )}
              {(entry.type === "CarveoutAdded" ||
                entry.type === "CarveoutRemoved" ||
                entry.type === "Updated") &&
                (entry.valueChanges as any)?.carveout?.name && (
                  <div className="text-sm mb-1">
                    <span className="font-medium text-900">Carve-out:</span>{" "}
                    <span className="text-700">
                      {(entry.valueChanges as any).carveout.name}
                    </span>
                  </div>
                )}
              {(entry.type === "AttachmentAdded" ||
                entry.type === "AttachmentRemoved") &&
                (entry.valueChanges as any)?.attachments?.name && (
                  <div className="text-sm mb-1">
                    <span className="font-medium text-900">Attachment:</span>{" "}
                    <span className="text-700">
                      {(entry.valueChanges as any).attachments.name}
                    </span>
                  </div>
                )}
              {entry.type === "Updated" &&
                entry.valueChanges &&
                Object.keys(entry.valueChanges).length > 0 && (
                  <ul className="list-none p-0 m-0 mt-2">
                    {Object.entries(entry.valueChanges)
                      // `carveout` names the file the other fields refer to; it
                      // is rendered above, not as a from/to row.
                      .filter(([field]) => field !== "carveout")
                      .map(([field, change]) => {
                        if (field === "rateCalculations") {
                          const { added, removed, changed } =
                            diffRateCalculations(change.from, change.to);
                          return (
                            <li key={field} className="text-sm mb-1">
                              <span className="font-medium text-900">
                                {HISTORY_FIELD_LABELS[field] ?? field}:
                              </span>
                              {added.length === 0 &&
                                removed.length === 0 &&
                                changed.length === 0 && (
                                  <span className="text-600"> No changes</span>
                                )}
                              <ul className="list-none p-0 m-0 mt-1">
                                {added.length > 0 && (
                                  <li className="text-green-600">
                                    <span className="font-medium">
                                      Base Rate File added:
                                    </span>{" "}
                                    {added.join(", ")}
                                  </li>
                                )}
                                {removed.length > 0 && (
                                  <li className="text-red-600">
                                    <span className="font-medium">
                                      Base Rate File removed:
                                    </span>{" "}
                                    {removed.join(", ")}
                                  </li>
                                )}
                                {changed.length > 0 && (
                                  <li className="text-blue-600">
                                    <span className="font-medium">
                                      Rate changed:
                                    </span>{" "}
                                    {changed.join(", ")}
                                  </li>
                                )}
                              </ul>
                            </li>
                          );
                        }
                        if (HISTORY_LIST_FIELDS.has(field)) {
                          const label = HISTORY_FIELD_LABELS[field] ?? field;
                          const { added, removed } = diffNamedList(
                            change.from,
                            change.to
                          );
                          return (
                            <li key={field} className="text-sm mb-1">
                              <span className="font-medium text-900">
                                {label}:
                              </span>
                              {added.length === 0 && removed.length === 0 && (
                                <span className="text-600"> No changes</span>
                              )}
                              <ul className="list-none p-0 m-0 mt-1">
                                {added.length > 0 && (
                                  <li className="text-green-600">
                                    <span className="font-medium">
                                      {label} added:
                                    </span>{" "}
                                    {added.join(", ")}
                                  </li>
                                )}
                                {removed.length > 0 && (
                                  <li className="text-red-600">
                                    <span className="font-medium">
                                      {label} removed:
                                    </span>{" "}
                                    {removed.join(", ")}
                                  </li>
                                )}
                              </ul>
                            </li>
                          );
                        }
                        return (
                          <li key={field} className="text-sm mb-1">
                            <span className="font-medium text-900">
                              {HISTORY_FIELD_LABELS[field] ?? field}:
                            </span>{" "}
                            <span className="text-600">
                              {formatHistoryValue(change.from, field)}
                            </span>
                            <i className="pi pi-arrow-right text-500 text-xs mx-2" />
                            <span className="text-900">
                              {formatHistoryValue(change.to, field)}
                            </span>
                          </li>
                        );
                      })}
                  </ul>
                )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center p-4 text-600">
          No history recorded for this contract yet.
        </div>
      )}
    </Card>
  );
}
